import React from "react";
import { Pressable, Text, View } from "react-native";

import { PhoneNumberText, PrimaryButton } from "../../../../common/native";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { formatTerminalSummaryReason } from "./CallSessionProvider";
import CallControls from "./CallControls";
import useCallSession from "./useCallSession";

const formatLiveDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const getHeadingByState = (state: string): string => {
  switch (state) {
    case "preparing_media":
    case "registering_client":
    case "outbound_creating":
      return "Calling...";
    case "outbound_ringing":
      return "Ringing patient...";
    case "connecting_media":
      return "Connecting call...";
    case "connected":
      return "Call in progress";
    case "ending":
      return "Ending call...";
    case "ended":
      return "Call ended";
    case "error":
      return "Call error";
    default:
      return "Call";
  }
};

const getBodyByState = (stateLabel: string, state: string): string => {
  if (state === "connected") {
    return "You are connected to the patient in browser audio mode.";
  }
  if (state === "ended") {
    return "";
  }
  if (state === "error") {
    return "The call flow failed. Review the error and try again.";
  }
  return stateLabel;
};

const focusableSelector =
  'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';

const CallSessionModal: React.FC = () => {
  const {
    state,
    stateLabel,
    error,
    activeCall,
    activeCallContext,
    isHangupSubmitting,
    isMuted,
    audioWarning,
    ringingAudioEnabled,
    connectedDurationSeconds,
    requestHangup,
    toggleMute,
    dismissSummary,
    retryAudio,
    setRingingAudioEnabled,
  } = useCallSession();

  const visible = state !== "idle" && state !== "incoming_ringing";
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = React.useRef<HTMLElement | null>(null);

  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);

    return () => {
      mediaQuery.removeEventListener("change", onChange);
    };
  }, []);

  React.useEffect(() => {
    if (!visible || typeof document === "undefined") {
      return;
    }

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const focusFirstInModal = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      const firstFocusable =
        dialog.querySelector<HTMLElement>(focusableSelector);
      firstFocusable?.focus();
    };

    focusFirstInModal();

    const onKeyDown = (event: KeyboardEvent) => {
      if (!dialogRef.current) {
        return;
      }

      if (event.key === "Escape" && (state === "ended" || state === "error")) {
        event.preventDefault();
        dismissSummary();
        return;
      }

      if ((event.key === "h" || event.key === "H") && state === "connected") {
        event.preventDefault();
        requestHangup().catch(() => undefined);
        return;
      }

      if ((event.key === "m" || event.key === "M") && state === "connected") {
        event.preventDefault();
        toggleMute();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.hasAttribute("disabled"));

      if (!focusableElements.length) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [dismissSummary, requestHangup, state, toggleMute, visible]);

  if (!visible || typeof document === "undefined") {
    return null;
  }

  const { createPortal } = require("react-dom");

  return createPortal(
    <div
      ref={overlayRef}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483000,
        backgroundColor: "rgba(17, 24, 39, 0.48)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: ADMIN_TOKENS.spacing.lg,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Call session"
        style={{
          width: "min(560px, calc(100vw - 32px))",
          borderRadius: ADMIN_TOKENS.radius.xl,
          border: `${ADMIN_TOKENS.borderWidth.sm}px solid ${ADMIN_TOKENS.color.border}`,
          backgroundColor: ADMIN_TOKENS.color.surface,
          padding: ADMIN_TOKENS.spacing.xl,
          display: "flex",
          flexDirection: "column",
          gap: ADMIN_TOKENS.spacing.md,
          transition: `transform ${prefersReducedMotion ? 0 : ADMIN_TOKENS.motion.standard}ms ease-out`,
          transform: "translateY(0)",
        }}
      >
        <div aria-live="polite">
          <Text
            style={{
              color: ADMIN_TOKENS.color.textPrimary,
              fontFamily: ADMIN_TOKENS.font.semibold,
              fontSize: ADMIN_TOKENS.fontSize.xl,
              lineHeight: ADMIN_TOKENS.lineHeight.xl,
            }}
          >
            {getHeadingByState(state)}
          </Text>
          <Text
            style={{
              color: ADMIN_TOKENS.color.textSecondary,
              fontFamily: ADMIN_TOKENS.font.regular,
              fontSize: ADMIN_TOKENS.fontSize.sm,
              lineHeight: ADMIN_TOKENS.lineHeight.sm,
              marginTop: ADMIN_TOKENS.spacing.xs,
            }}
          >
            {getBodyByState(stateLabel, state)}
          </Text>
        </div>

        {activeCallContext ? (
          <View
            style={{
              borderRadius: ADMIN_TOKENS.radius.md,
              borderWidth: ADMIN_TOKENS.borderWidth.sm,
              borderColor: ADMIN_TOKENS.color.borderMuted,
              padding: ADMIN_TOKENS.spacing.md,
              gap: ADMIN_TOKENS.spacing.xs,
              backgroundColor: ADMIN_TOKENS.color.surfaceMuted,
            }}
          >
            <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
              Call #{activeCallContext.callId}
            </Text>
            <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
              From:{" "}
              <PhoneNumberText
                value={activeCallContext.fromE164}
                style={{ color: ADMIN_TOKENS.color.textSecondary }}
              />
            </Text>
            <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
              To:{" "}
              <PhoneNumberText
                value={activeCallContext.toE164}
                style={{ color: ADMIN_TOKENS.color.textSecondary }}
              />
            </Text>
            <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
              Status: {activeCall?.status || activeCallContext.latestStatus}
            </Text>
            {state === "connected" ? (
              <Text
                style={{
                  color: ADMIN_TOKENS.color.textPrimary,
                  fontFamily: ADMIN_TOKENS.font.medium,
                }}
              >
                Duration: {formatLiveDuration(connectedDurationSeconds)}
              </Text>
            ) : null}
          </View>
        ) : null}

        {audioWarning && state === "outbound_ringing" ? (
          <View
            style={{
              borderRadius: ADMIN_TOKENS.radius.md,
              borderWidth: ADMIN_TOKENS.borderWidth.sm,
              borderColor: ADMIN_TOKENS.color.errorBorder,
              backgroundColor: ADMIN_TOKENS.color.errorSurface,
              padding: ADMIN_TOKENS.spacing.md,
              gap: ADMIN_TOKENS.spacing.sm,
            }}
          >
            <Text style={{ color: ADMIN_TOKENS.color.errorTextStrong }}>
              {audioWarning}
            </Text>
            <Pressable
              onPress={() => retryAudio().catch(() => undefined)}
              accessibilityRole="button"
              style={({ pressed }) => ({
                minHeight: 44,
                borderRadius: ADMIN_TOKENS.radius.md,
                borderWidth: ADMIN_TOKENS.borderWidth.sm,
                borderColor: pressed
                  ? ADMIN_TOKENS.color.accent
                  : ADMIN_TOKENS.color.border,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: ADMIN_TOKENS.color.surface,
                paddingHorizontal: ADMIN_TOKENS.spacing.md,
                paddingVertical: ADMIN_TOKENS.spacing.sm,
              })}
            >
              <Text
                style={{
                  color: ADMIN_TOKENS.color.textPrimary,
                  fontFamily: ADMIN_TOKENS.font.medium,
                }}
              >
                Retry Sound
              </Text>
            </Pressable>
          </View>
        ) : null}

        {state === "connected" ? (
          <CallControls
            isHangupSubmitting={isHangupSubmitting}
            isMuted={isMuted}
            onHangup={() => requestHangup().catch(() => undefined)}
            onToggleMute={toggleMute}
          />
        ) : null}

        {(state === "ended" || state === "error") && activeCall ? (
          <View
            style={{
              borderRadius: ADMIN_TOKENS.radius.md,
              borderWidth: ADMIN_TOKENS.borderWidth.sm,
              borderColor:
                state === "error"
                  ? ADMIN_TOKENS.color.errorBorder
                  : ADMIN_TOKENS.color.borderMuted,
              backgroundColor:
                state === "error"
                  ? ADMIN_TOKENS.color.errorSurface
                  : ADMIN_TOKENS.color.surfaceMuted,
              padding: ADMIN_TOKENS.spacing.md,
              gap: ADMIN_TOKENS.spacing.xs,
            }}
          >
            <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
              Final status: {activeCall.status}
            </Text>
            {typeof activeCall.duration_seconds === "number" ? (
              <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
                Duration: {formatLiveDuration(activeCall.duration_seconds)}
              </Text>
            ) : null}
            {formatTerminalSummaryReason(activeCall) ? (
              <Text style={{ color: ADMIN_TOKENS.color.textSecondary }}>
                Reason: {formatTerminalSummaryReason(activeCall)}
              </Text>
            ) : null}
          </View>
        ) : null}

        {error ? (
          <Text
            style={{
              color: ADMIN_TOKENS.color.errorTextStrong,
              fontFamily: ADMIN_TOKENS.font.medium,
            }}
          >
            {error}
          </Text>
        ) : null}

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: ADMIN_TOKENS.spacing.sm,
          }}
        >
          <input
            id="ringing-audio-enabled"
            type="checkbox"
            checked={ringingAudioEnabled}
            onChange={(event) => setRingingAudioEnabled(event.target.checked)}
            style={{ width: 18, height: 18 }}
          />
          <label
            htmlFor="ringing-audio-enabled"
            style={{
              color: ADMIN_TOKENS.color.textSecondary,
              fontFamily: ADMIN_TOKENS.font.regular,
              fontSize: ADMIN_TOKENS.fontSize.sm,
            }}
          >
            Enable ringing audio
          </label>
        </View>

        {state === "ended" || state === "error" ? (
          <PrimaryButton text="Dismiss" onPress={dismissSummary} fullWidth />
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default CallSessionModal;
